import { Group, Vector3 } from 'three'
import { Line2 } from 'three/examples/jsm/lines/Line2.js'
import { LineMaterial, type LineMaterialParameters } from 'three/examples/jsm/lines/LineMaterial.js'
import type { GraphicsBackendKind } from '@/graphics/GraphicsConfig'
import {
  createCadLineMaterialSync,
  createCadLineObjectSync,
  getCadLineBackend,
  isCadLine2Object,
} from '@/plugins/cad/module/CADModule/object/material/CadLineFactory'
import { MaterialUtils } from '@/plugins/cad/module/CADModule/object/material/MaterialUtils'
import { Setting } from '@/plugins/cad/module/common/setting'
import { DrawUtils, type IPoint, type MaterialOption } from '@/plugins/cad/module/utils'
import type { UnitPresentationService } from '@/runtime/UnitPresentationService'
import { DEFAULT_UNIT_PREFERENCE, formatLength } from '@/shared/unitPresentation'
import { beginDisposeOnce } from '@/shared/utils/disposeOnce'

/** 线段标注分组配置选项，控制长度/角度标注显示与样式 */
export interface ILineDimensionGroupOptions {
  /** 是否显示长度标注 */
  showLength?: boolean
  /** 是否显示角度标注 */
  showAngle?: boolean
  /** 是否显示箭头 */
  showArrow?: boolean
  /** 长度标注线的偏移距离，单位世界坐标 */
  lengthOffset?: number
  /** 角度标注弧线半径，未设置时使用线段长度 */
  angleRadius?: number
  /** 长度文本保留的小数位数 */
  lengthDecimals?: number
  /** 角度文本保留的小数位数 */
  angleDecimals?: number
  /** 长度与角度通用的小数位数，优先级高于单独设置 */
  decimals?: number
  /** 标注文本字号，单位像素 */
  textSize?: number
  /** 箭头大小，单位世界坐标 */
  arrowSize?: number
  /** 标注颜色，十六进制整型 */
  textColor?: number
  /** 标注线材质选项，见 MaterialOption */
  materialOption?: MaterialOption
  /** 虚线段长度，单位世界坐标 */
  dashSize?: number
}

const defaultOptions: ILineDimensionGroupOptions = {
  showLength: true,
  showAngle: true,
  showArrow: false,
  lengthOffset: 0.5,
  lengthDecimals: 2,
  angleDecimals: 0,
  arrowSize: 0.15,
}

/**
 * 线段标注（例如绘制过程中的实时长度标注、角度）
 *
 */
export default class LineDimensionGroup extends Group {
  private start: Vector3 = new Vector3()
  private end: Vector3 = new Vector3()
  private direction: Vector3 = new Vector3()
  private lengthDimensionGroup: Group | undefined
  private angleDimensionGroup: Group | undefined
  private elementContainer = document.createElement('div')
  private lengthTextElement: HTMLElement | undefined
  private angleTextElement: HTMLElement | undefined
  private material: LineMaterial
  private readonly lineBackend: GraphicsBackendKind
  private readonly unsubscribeUnitPreference?: () => void

  private options = {
    ...defaultOptions,
    textSize: Setting.dimensionConfig.textSize,
    textColor: Setting.dimensionConfig.textColor,
    dashSize: Setting.dashSize,
  }

  constructor(
    startPoint: IPoint,
    endPoint: IPoint,
    options?: ILineDimensionGroupOptions,
    parentGroup?: Group,
    parentElement?: HTMLElement,
    private transformToScreen?: (point: IPoint) => { x: number; y: number },
    private getPixelSizeInWorld?: (radius: number) => number,
    private readonly unitPresentation?: UnitPresentationService
  ) {
    super()
    this.lineBackend = getCadLineBackend()
    options && Object.assign(this.options, options)
    const dashSize = this.getPixelSizeInWorld?.(this.options.dashSize) ?? this.options.dashSize
    const materialOption = {
      color: Setting.dimensionConfig.color,
      transparent: Setting.dimensionConfig.opacity < 1,
      opacity: Setting.dimensionConfig.opacity,
      linewidth: Setting.dimensionConfig.linewidth,
      dashed: true,
      resolution: MaterialUtils.globalResolution,
      dashSize,
      gapSize: dashSize / 2,
      depthTest: false,
      depthWrite: false,
      ...(this.options.materialOption || {}),
    }
    this.options.materialOption = materialOption
    this.unsubscribeUnitPreference = this.unitPresentation?.subscribe(() => {
      this.updateLengthDimension()
    })
    this.material = createCadLineMaterialSync({
      backend: this.lineBackend,
      options: materialOption as LineMaterialParameters,
      createWebGlMaterial: (options) => new LineMaterial(options),
    })
    this.updateStartAndEnd(startPoint, endPoint)
    this.init()
    parentGroup?.add(this)
    parentElement?.appendChild(this.elementContainer)
  }

  private get lengthDecimals() {
    return this.options.decimals ?? this.options.lengthDecimals
  }

  private get angleDecimals() {
    return this.options.decimals ?? this.options.angleDecimals
  }

  private get materialOption() {
    return this.options.materialOption || {}
  }

  private init() {
    if (this.options.showLength) {
      this.createLengthDimension()
    }
    if (this.options.showAngle) {
      this.createAngleDimension()
    }
    this.elementContainer.className = 'line-dimension-container'
    Object.assign(this.elementContainer.style, {
      position: 'absolute',
      left: 0,
      top: 0,
      pointerEvents: 'none',
    })
  }

  // 更新起点和终点位置
  private updateStartAndEnd(startPoint: IPoint, endPoint: IPoint) {
    this.start.copy({ ...startPoint, z: startPoint.z ?? 0 })
    this.end.copy({ ...endPoint, z: endPoint.z ?? 0 })
    this.direction.subVectors(this.end, this.start)
  }

  private drawLineObject(points: IPoint[], parent: Group) {
    const geometry = DrawUtils.getLineGeometryByPoints(points)
    const line = createCadLineObjectSync({
      backend: this.lineBackend,
      geometry,
      material: this.material,
      createWebGlObject: () => new Line2(geometry, this.material),
    })
    line.computeLineDistances()
    line.renderOrder = 999
    parent.add(line)
    return line
  }

  private updateLineObject(line: Line2 | undefined, points: IPoint[]) {
    if (!line) return
    line.geometry.dispose()
    line.geometry = DrawUtils.getLineGeometryByPoints(points)
    line.computeLineDistances()
  }

  private getLengthDimensionData() {
    const offset = this.options.lengthOffset ?? 0.5
    const { start, end, direction } = this
    const length = direction.length()
    const dirNormalized = direction.clone().normalize()
    const normal = new Vector3(-dirNormalized.y, dirNormalized.x, 0)

    // 标注线的起点和终点（偏移到线段上方）
    const dimStart = start.clone().add(normal.clone().multiplyScalar(offset))
    const dimEnd = end.clone().add(normal.clone().multiplyScalar(offset))

    // 文本数据
    const text = formatLength(
      length,
      this.unitPresentation?.preference ?? DEFAULT_UNIT_PREFERENCE
    ).valueText
    const textPos = new Vector3().addVectors(dimStart, dimEnd).multiplyScalar(0.5)

    return { start, end, dimStart, dimEnd, text, textPos }
  }

  private createLengthDimension() {
    const group = new Group()
    const { start, end, dimStart, dimEnd, text, textPos } = this.getLengthDimensionData()

    // 绘制标注线
    const dimLine = this.drawLineObject(
      [
        { x: dimStart.x, y: dimStart.y, z: dimStart.z },
        { x: dimEnd.x, y: dimEnd.y, z: dimEnd.z },
      ],
      group
    )
    dimLine.name = 'LengthDimensionLine'

    // 绘制延伸线
    const extensionStart1 = this.drawLineObject(
      [
        { x: start.x, y: start.y, z: start.z },
        { x: dimStart.x, y: dimStart.y, z: dimStart.z },
      ],
      group
    )
    const extensionStart2 = this.drawLineObject(
      [
        { x: end.x, y: end.y, z: end.z },
        { x: dimEnd.x, y: dimEnd.y, z: dimEnd.z },
      ],
      group
    )
    extensionStart1.name = 'LengthDimensionExtensionLine1'
    extensionStart2.name = 'LengthDimensionExtensionLine2'

    // 添加长度文本
    this.lengthTextElement = this.createText(text, textPos)
    this.elementContainer.appendChild(this.lengthTextElement)

    this.add(group)
    this.lengthDimensionGroup = group
    return group
  }

  // 更新长度标注的实现
  private updateLengthDimension() {
    if (!this.lengthDimensionGroup) return

    const dimLine = this.lengthDimensionGroup.getObjectByName('LengthDimensionLine') as
      | Line2
      | undefined
    const extensionLine1 = this.lengthDimensionGroup.getObjectByName(
      'LengthDimensionExtensionLine1'
    ) as Line2 | undefined
    const extensionLine2 = this.lengthDimensionGroup.getObjectByName(
      'LengthDimensionExtensionLine2'
    ) as Line2 | undefined

    const { start, end, dimStart, dimEnd, text, textPos } = this.getLengthDimensionData()

    this.updateLineObject(dimLine, [dimStart, dimEnd])
    this.updateLineObject(extensionLine1, [start, dimStart])
    this.updateLineObject(extensionLine2, [end, dimEnd])

    if (this.lengthTextElement) {
      this.updateText(this.lengthTextElement, text, textPos)
    }
  }

  private getAngleDimensionData() {
    const { start, direction } = this
    const length = direction.length()
    const angleRadians = Math.atan2(direction.y, direction.x)
    const angleRadius = this.options.angleRadius ?? length
    const center = start
    const xAxisEnd = center.clone().add(new Vector3(angleRadius, 0, 0))

    // 文本数据
    const angleDegrees = (angleRadians * 180) / Math.PI
    const text = `${angleDegrees.toFixed(this.angleDecimals)}°`
    const midAngle = angleRadians / 2
    const textPos = new Vector3(
      center.x + Math.cos(midAngle) * angleRadius,
      center.y + Math.sin(midAngle) * angleRadius,
      center.z
    )

    return {
      center: start,
      radius: angleRadius,
      angleRadians,
      xAxisEnd,
      text,
      textPos,
    }
  }

  private createAngleDimension() {
    const group = new Group()
    const { center, radius, angleRadians, xAxisEnd, text, textPos } = this.getAngleDimensionData()

    // 绘制X轴参考线
    const xAxisLine = this.drawLineObject(
      [
        { x: center.x, y: center.y, z: center.z },
        { x: xAxisEnd.x, y: xAxisEnd.y, z: xAxisEnd.z },
      ],
      group
    )
    xAxisLine.name = 'AngleDimensionXAxis'

    // 绘制角度弧线
    const points = DrawUtils.getArcPoints({
      center: { x: 0, y: 0 },
      radius,
      startAngle: 0,
      endAngle: angleRadians,
      clockwise: angleRadians < 0,
    })
    const arc = this.drawLineObject(points, group)
    arc.position.copy(center)
    arc.name = 'AngleDimensionArc'
    group.add(arc)

    // 在弧线中点添加角度文本
    this.angleTextElement = this.createText(text, { x: textPos.x, y: textPos.y })
    this.elementContainer.appendChild(this.angleTextElement)

    this.add(group)
    this.angleDimensionGroup = group
    return group
  }

  private updateAngleDimension() {
    if (!this.angleDimensionGroup) return

    const xAxisLine = this.angleDimensionGroup.getObjectByName('AngleDimensionXAxis') as
      | Line2
      | undefined
    const arc = this.angleDimensionGroup.getObjectByName('AngleDimensionArc') as Line2 | undefined

    const { center, radius, angleRadians, xAxisEnd, text, textPos } = this.getAngleDimensionData()

    this.updateLineObject(xAxisLine, [center, xAxisEnd])
    this.updateLineObject(
      arc,
      DrawUtils.getArcPoints({
        center: { x: 0, y: 0 },
        radius,
        startAngle: 0,
        endAngle: angleRadians,
        clockwise: angleRadians < 0,
      })
    )
    arc?.position.copy(center)
    if (this.angleTextElement) {
      this.updateText(this.angleTextElement, text, textPos)
    }
  }

  private createText(text: string, position: { x: number; y: number }) {
    const screenPos = this.transformToScreen?.({ x: position.x, y: position.y, z: 0 }) ?? position
    const el = document.createElement('div')
    el.innerText = text
    el.className = 'dimension-item'
    Object.assign(el.style, {
      position: 'absolute',
      left: `${screenPos.x}px`,
      top: `${screenPos.y}px`,
      transform: `translate(-50%, -100%)`,
      color: `${this.options.textColor}`,
      fontSize: `${this.options.textSize}px`,
      whiteSpace: 'nowrap',
    })
    el.dataset.x = position.x.toString()
    el.dataset.y = position.y.toString()
    return el
  }

  private updateText(el: HTMLElement, text: string, position: { x: number; y: number }) {
    const screenPos = this.transformToScreen?.({ x: position.x, y: position.y, z: 0 }) ?? position
    Object.assign(el.style, {
      left: `${screenPos.x}px`,
      top: `${screenPos.y}px`,
    })
    el.innerText = text
    el.dataset.x = position.x.toString()
    el.dataset.y = position.y.toString()
  }

  private updateTextScreenPosition(el: HTMLElement) {
    if (!this.transformToScreen) return

    const position = { x: parseFloat(el.dataset.x!), y: parseFloat(el.dataset.y!) }
    const screenPos = this.transformToScreen?.({ x: position.x, y: position.y, z: 0 }) ?? position
    Object.assign(el.style, {
      left: `${screenPos.x}px`,
      top: `${screenPos.y}px`,
    })
  }

  /**
   * 动态更新标注配置。
   *
   * @param options - 需要覆盖的配置字段，未传字段保持原值
   */
  updateOptions(options: Partial<ILineDimensionGroupOptions>) {
    const needUpdateLength =
      options.showLength !== undefined && options.showLength !== this.options.showLength
    const needUpdateAngle =
      options.showAngle !== undefined && options.showAngle !== this.options.showAngle

    Object.assign(this.options, options)

    if (needUpdateLength) {
      const isShowLength = !!this.options.showLength
      if (isShowLength && !this.lengthDimensionGroup) {
        this.createLengthDimension()
      } else if (this.lengthDimensionGroup) {
        this.lengthDimensionGroup.visible = isShowLength
      }
      if (this.lengthTextElement) {
        this.lengthTextElement.style.visibility = isShowLength ? '' : 'hidden'
      }
    }
    if (needUpdateAngle) {
      const isShowAngle = !!this.options.showAngle
      if (isShowAngle && !this.angleDimensionGroup) {
        this.createAngleDimension()
      } else if (this.angleDimensionGroup) {
        this.angleDimensionGroup.visible = isShowAngle
      }
      if (this.angleTextElement) {
        this.angleTextElement.style.visibility = isShowAngle ? '' : 'hidden'
      }
    }
  }

  /**
   * 更新线段两端点并刷新长度与角度标注。
   *
   * @param startPoint - 线段起点
   * @param endPoint - 线段终点
   */
  updateLine(startPoint: IPoint, endPoint: IPoint) {
    this.updateStartAndEnd(startPoint, endPoint)

    // 更新长度标注
    this.updateLengthDimension()

    // 更新角度标注
    this.updateAngleDimension()
  }

  /** 按当前像素比刷新虚线样式与屏幕空间文本位置，建议每帧调用 */
  update() {
    // 更新虚线
    if (this.getPixelSizeInWorld) {
      const dashSize = this.getPixelSizeInWorld(Setting.dashSize)
      const gapSize = dashSize / 2
      this.traverse((child) => {
        if (isCadLine2Object(child)) {
          child.material.dashSize = dashSize
          child.material.gapSize = gapSize
          child.computeLineDistances()
        }
      })
    }

    // 更新文字位置
    this.lengthTextElement && this.updateTextScreenPosition(this.lengthTextElement)
    this.angleTextElement && this.updateTextScreenPosition(this.angleTextElement)
  }

  /** 释放几何、材质、DOM 节点并从父级移除本分组 */
  dispose() {
    if (!beginDisposeOnce(this)) return

    // Three r186 起 Object3D 才提供 dispose()，仅用于派发 dispose 事件；旧 peer 无该方法，必须可选调用。
    super.dispose?.()
    this.unsubscribeUnitPreference?.()
    DrawUtils.disposeObject(this.lengthDimensionGroup)
    DrawUtils.disposeObject(this.angleDimensionGroup)
    this.material.dispose()
    this.lengthDimensionGroup = undefined
    this.angleDimensionGroup = undefined
    this.parent?.remove(this)
    this.clear()

    this.elementContainer.innerHTML = ''
    this.elementContainer.remove()
    this.lengthTextElement = undefined
    this.angleTextElement = undefined
  }
}
